import type { RouteRecordRaw } from 'vue-router';
import { h } from 'vue';
import { RouterView } from 'vue-router';
import type { MenuItem } from '@/types/api/menu.type';
import { getIconByPath } from './icons/iconMap';

// 导入布局组件
const Layout = () => import('@/layouts/index.vue');

// 简单的 RouterView 包装器，用于嵌套路由
const ParentLayout = {
  name: 'ParentLayout',
  render: () => h(RouterView)
};

// 视图文件映射表
const viewModules = import.meta.glob('@/views/**/*.vue');

// 缓存已找到的组件路径，避免重复搜索
const componentCache = new Map<string, () => Promise<unknown>>();

/**
 * 将名称转换为驼峰格式（用于目录名）
 * 例如:
 * - "User Management" -> "userManagement"
 * - "Partners" -> "partners"
 * - "API" -> "api"
 * - "SystemConfig" -> "systemConfig"
 */
function nameToCamelCase(name: string): string {
  // 如果没有空格
  if (!name.includes(' ')) {
    // 全大写的情况（如 API），转成全小写
    if (name === name.toUpperCase()) {
      return name.toLowerCase();
    }

    // 其他情况，首字母小写返回（如 Partners -> partners, SystemConfig -> systemConfig）
    return name.charAt(0).toLowerCase() + name.slice(1);
  }

  // 有空格，转换为驼峰
  return name
    .split(/\s+/)
    .map((word, index) => {
      const lower = word.toLowerCase().replace(/[^a-z0-9]/g, '');

      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * 根据路径获取组件
 * @param componentPath 组件路径（如 /member）
 * @param parentName 父菜单名称（如 "User Management"）
 *
 * 路径拼接规则：
 * - 如果有 parentName，拼接为：views/{parentName驼峰}/{componentPath}/index.vue
 * - 例如：parentName="User Management", componentPath="/member"
 * - 拼接结果：views/userManagement/member/index.vue
 */
function getComponent(componentPath: string, parentName?: string) {
  // 如果是根路径或没有路径，返回 Layout
  if (!componentPath || componentPath === '/') {
    return Layout;
  }

  // 移除开头的斜杠
  const cleanPath = componentPath.startsWith('/') ? componentPath.slice(1) : componentPath;

  // 构建完整路径
  let fullViewPath: string;

  if (parentName) {
    // 有父菜单名称，使用父名称（驼峰）+ 组件路径
    const parentDir = nameToCamelCase(parentName);

    fullViewPath = `${parentDir}/${cleanPath}`;
  } else {
    // 没有父菜单，直接使用组件路径
    fullViewPath = cleanPath;
  }

  // 生成缓存 key
  const cacheKey = fullViewPath;

  // 检查缓存
  if (componentCache.has(cacheKey)) {
    return componentCache.get(cacheKey)!;
  }

  // 尝试匹配的文件路径
  const possiblePaths = [
    `/src/views/${fullViewPath}/index.vue`,
    `/src/views/${fullViewPath}.vue`
  ];

  for (const filePath of possiblePaths) {
    if (viewModules[filePath]) {
      componentCache.set(cacheKey, viewModules[filePath] as () => Promise<unknown>);
      //  ${componentPath} -> ${filePath}

      return viewModules[filePath];
    }
  }

  return () => import('@/views/error/404.vue');
}

/**
 * 将菜单名转换为路由路径（驼峰格式）
 * 例如: "User Management" -> "/userManagement"
 */
function nameToPath(name: string): string {
  return '/' + name
    .split(/\s+/)
    .map((word, index) => {
      const lower = word.toLowerCase().replace(/[^a-z0-9]/g, '');

      // 第一个单词小写，后续单词首字母大写
      return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * 将菜单项转换为路由配置
 * @param menu 菜单项
 * @param isChild 是否为子路由
 * @param parentName 父菜单名称（用于拼接组件路径）
 */
function menuToRoute(menu: MenuItem, isChild = false, parentName?: string): RouteRecordRaw | null {
  // 获取所有子菜单（包括 hidden 的，路由都要添加）
  const menuChildren = menu.children || [];
  const hasChildren = menuChildren.length > 0;

  // 生成路由路径
  // 如果有 component，使用 component；否则根据 name 生成
  let routePath = menu.component || nameToPath(menu.name);

  // 子路由需要移除开头的斜杠
  if (isChild && routePath.startsWith('/')) {
    routePath = routePath.slice(1);
  }

  // 生成路由名称（使用 id 确保唯一性）
  let routeName: string;

  if (menu.component) {
    // 有 component 的菜单，用 component 生成名称
    routeName = menu.component
      .replace(/^\//, '')
      .split('/')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join('');
  } else {
    // 没有 component 的父级菜单，用 name + id 生成唯一名称
    routeName = `${menu.name.replace(/\s+/g, '')}Parent${menu.id}`;
  }

  // 构建路由配置
  const route: any = {
    path: routePath,
    name: routeName || menu.id,
    meta: {
      title: menu.name, // 使用 name 字段作为标题
      icon: getIconByPath(menu.component || menu.icon), // 根据路径自动匹配图标
      orderBy: menu.sort,
      hidden: menu.hidden === 1, // hidden=1 表示隐藏
      affix: false,
      alwaysShow: hasChildren, // 有子菜单时始终显示父级
      // 提取按钮权限的 alias 列表
      buttons: menu.menuBtn?.filter(btn => btn.hasPermission).map(btn => btn.alias) || []
    }
  };

  if (hasChildren) {
    // 有子菜单的情况，使用 ParentLayout 作为容器
    route.component = ParentLayout;
    // 递归时传入当前菜单的 name 作为子菜单的 parentName
    route.children = menuChildren
      .map(child => menuToRoute(child, true, menu.name))
      .filter(Boolean) as RouteRecordRaw[];

    // 设置重定向到第一个可见的子路由
    const firstVisibleChild = menuChildren.find(child => child.hidden !== 1 && child.component);

    if (firstVisibleChild?.component) {
      // 子路由路径不带开头斜杠
      const childPath = firstVisibleChild.component.startsWith('/')
        ? firstVisibleChild.component.slice(1)
        : firstVisibleChild.component;

      route.redirect = `${routePath}/${childPath}`;
    }
  } else {
    // 没有子菜单（叶子节点），直接设置组件
    if (!menu.component) {
      return null;
    }

    // 传入 parentName 用于拼接组件路径
    route.component = getComponent(menu.component, parentName);
  }

  return route;
}

/**
 * 递归提取所有按钮权限的 alias
 * @param menus 菜单列表
 */
export function extractButtonAliases(menus: MenuItem[]): string[] {
  const aliases: string[] = [];

  function traverse(menuList: MenuItem[]) {
    menuList.forEach(menu => {
      // 提取当前菜单的按钮权限
      if (menu.menuBtn) {
        menu.menuBtn.forEach(btn => {
          if (btn.hasPermission && btn.alias) {
            aliases.push(btn.alias);
          }
        });
      }

      // 递归处理子菜单
      if (menu.children && menu.children.length > 0) {
        traverse(menu.children);
      }
    });
  }

  traverse(menus);

  return aliases;
}

/**
 * 将菜单列表转换为路由配置
 * @param menuList 菜单列表
 */
export function generateRoutesFromMenus(menuList: MenuItem[]): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = [];

  // 转换菜单为路由对象（hidden 的菜单也要添加路由，只是侧边栏不显示）
  menuList.forEach(menu => {
    // 过滤掉 Dashboard，因为已经在静态路由中定义了 || menu.name === 'Dashborad'
    if (menu.component === '/dashboard' || menu.name === 'Dashboard') {
      return;
    }

    const route = menuToRoute(menu, false);

    if (route) {
      routes.push(route);
    }
  });

  // 添加 404 路由（放在最后）
  routes.push({
    path: '/:pathMatch(.*)*',
    redirect: '/404',
    meta: { hidden: true }
  });

  return routes;
}

interface ItemMenu {
  name: string;
  children?: ItemMenu[];
}
export function filterMenuByNames(
  menus: MenuItem[],
  rules: ItemMenu[]
): MenuItem[] {
  const ruleMap = new Map<string, ItemMenu>(
    rules.map(r => [r.name, r])
  );

  function match(menu: MenuItem, rule?: ItemMenu): MenuItem | null {
    const matchedRule = rule ?? ruleMap.get(menu.name);

    if (!matchedRule) return null;

    const cloned = { ...menu };

    // case1: rule 没有 children → 返回整个菜单（带原 children）
    if (!matchedRule.children || matchedRule.children.length === 0) {
      return cloned;
    }

    // case2: rule 指定了 children → 递归过滤 menu.children
    if (menu.children && menu.children.length > 0) {
      const childRuleMap = new Map<string, ItemMenu>(
        matchedRule.children.map(r => [r.name, r])
      );

      const filteredChildren = menu.children
        .map(child => match(child, childRuleMap.get(child.name)))
        .filter(Boolean) as MenuItem[];

      return { ...cloned, children: filteredChildren };
    }

    return null;
  }

  // 开始过滤
  return menus
    .map(menu => match(menu))
    .filter(Boolean) as MenuItem[];
}